import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { fmtUSD } from '@/lib/utils';
import { Loader2, Check, BrainCircuit, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanPicker() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(null);
  const [interval, setInterval] = useState('monthly'); // 'monthly' | 'yearly'
  const [searchParams] = useSearchParams();
  const fromRegister = searchParams.get('from') === 'register';
  const cancelled = searchParams.get('billing') === 'cancelled';
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/billing/plans`, { credentials: 'include' });
        if (res.ok) setPlans(await res.json());
      } catch (e) {}
      finally { setLoading(false); }
    })();
  }, []);

  const subscribe = async (planId) => {
    setSubscribing(planId);
    try {
      const res = await fetch(`${API}/billing/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ plan_id: planId, interval }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.url) {
        window.location.href = data.url;   // → Stripe-hosted Checkout
        return;
      }
      toast.error(data.detail || 'Could not start checkout. Please try again.');
    } catch (e) {
      toast.error('Could not start checkout. Please try again.');
    }
    setSubscribing(null);
  };

  // Yearly toggle only shows once at least one plan has yearly pricing configured.
  const yearlyAvailable = plans.some((p) => p?.intervals?.yearly?.available === true);
  // Order tiers Starter → Business → Scale (by name; monthly price as fallback for any other plan).
  const PLAN_RANK = { starter: 0, business: 1, scale: 2 };
  const monthlyPrice = (p) => p?.intervals?.monthly?.price_cad ?? p?.monthly_price_cad ?? 0;
  const rankOf = (p) => PLAN_RANK[(p?.name || '').trim().toLowerCase()] ?? 100;
  const sortedPlans = [...plans].sort((a, b) => (rankOf(a) - rankOf(b)) || (monthlyPrice(a) - monthlyPrice(b)));

  return (
    <div className="min-h-screen bg-[#F7FAFB]" data-testid="plan-picker">
      {/* Standalone top bar — the paywall renders without AppLayout, so there
          is no sidebar, no menu, no dashboard access until the subscription
          activates. Only the logo, signed-in identity, and a sign-out escape. */}
      <header className="bg-white border-b border-[#E2E8ED]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <img src="/nivela-logo.png" alt="Nivela" className="h-7" />
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="text-[11px] text-[#72A4BF] hidden sm:inline" data-testid="paywall-user-email">
                Signed in as <span className="text-[#0D1830] font-medium">{user.email}</span>
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={handleSignOut}
              className="h-8 text-[11px] gap-1.5 text-[#446E87] hover:text-[#0D1830]"
              data-testid="paywall-sign-out">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-black tracking-tight text-[#0D1830]">
          {fromRegister ? 'Welcome — pick a plan to activate your account' : 'Choose your plan'}
        </h1>
        <p className="text-sm text-[#72A4BF] mt-1">
          {fromRegister
            ? "You'll be redirected to Stripe to enter your payment details. Your name and email are pre-filled — you only enter card information."
            : 'Each plan grants a monthly allowance of Nivela Tokens. If you run out, overage is billed at renewal.'}
        </p>
        {cancelled && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="checkout-cancelled-banner">
            Checkout was cancelled. Pick a plan when you're ready — your account stays locked until payment is complete.
          </div>
        )}

        {/* Monthly / Yearly toggle — only rendered when at least one plan has
            yearly pricing configured (intervals.yearly.available). The choice is
            sent to /billing/checkout so the right pair of Stripe Prices is attached. */}
        {yearlyAvailable && (
        <div className="mt-6 flex justify-center">
          <div className="inline-flex items-center rounded-full border border-[#E2E8ED] bg-white p-1 text-xs font-medium" data-testid="billing-interval-toggle">
            {['monthly', 'yearly'].map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setInterval(opt)}
                className={`px-4 py-1.5 rounded-full transition-colors capitalize ${
                  interval === opt
                    ? 'bg-[#0D1830] text-white'
                    : 'text-[#446E87] hover:text-[#0D1830]'
                }`}
                data-testid={`interval-${opt}`}>
                {opt}
              </button>
            ))}
          </div>
        </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center py-24 text-sm text-[#72A4BF]">
            No plans are available yet. Please contact your Nivela administrator.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {sortedPlans.map((p) => {
              // Pull the selected interval's numbers from the per-plan
              // `intervals` block returned by /billing/plans, with a safe
              // fall-through to the legacy top-level fields for back-compat.
              const ivl = (p.intervals && p.intervals[interval]) || {};
              const available = ivl.available !== false;
              const priceCad = ivl.price_cad != null
                ? ivl.price_cad
                : (interval === 'monthly' ? p.monthly_price_cad : null);
              const cycleLabel = interval === 'monthly' ? 'month' : 'year';
              const isHighlighted = p.highlight === 'most_popular';
              return (
                <Card
                  key={p.plan_id}
                  className={`shadow-none flex flex-col relative ${
                    isHighlighted
                      ? 'border-2 border-[#1D4053] ring-2 ring-[#1D4053]/10'
                      : 'border border-[#E2E8ED]'
                  }`}
                  data-testid={`plan-${p.plan_id}`}>
                  {isHighlighted && (
                    <span
                      className="absolute -top-2.5 right-4 bg-[#1D4053] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
                      data-testid={`most-popular-${p.plan_id}`}>
                      Most popular
                    </span>
                  )}
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2">
                      <BrainCircuit className="w-4 h-4 text-[#1D4053]" />
                      <h3 className="text-base font-bold text-[#0D1830]">{p.name}</h3>
                    </div>
                    {p.description && (
                      <p className="text-xs text-[#72A4BF] mt-1.5 leading-snug" data-testid={`description-${p.plan_id}`}>
                        {p.description}
                      </p>
                    )}
                    <div className="mt-4">
                      <span className="text-3xl font-black text-[#0D1830]">{fmtUSD(priceCad || 0)}</span>
                      <span className="text-xs text-[#72A4BF]"> CAD / {cycleLabel}</span>
                    </div>
                    <ul className="mt-5 space-y-2 text-xs text-[#446E87] flex-1">
                      {/* Stripe Product marketing_features — verbatim from
                          the Stripe Dashboard, identical copy to what their
                          Pricing Table renders. */}
                      {(p.features || []).map((feat, i) => (
                        <li key={`f-${i}`} className="flex items-start gap-2" data-testid={`feature-${p.plan_id}-${i}`}>
                          <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      onClick={() => subscribe(p.plan_id)}
                      disabled={subscribing != null || !available}
                      className={`mt-6 w-full text-white rounded-full hover:opacity-90 disabled:opacity-50 ${
                        isHighlighted ? 'bg-[#1D4053]' : 'bg-[#0D1830]'
                      }`}
                      data-testid={`subscribe-${p.plan_id}`}
                    >
                      {subscribing === p.plan_id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                      {available ? 'Subscribe' : 'Not available'}
                    </Button>
                    {!available && (
                      <p className="text-[10px] text-[#72A4BF] text-center mt-2" data-testid={`unavailable-${p.plan_id}-${interval}`}>
                        {interval === 'yearly' ? 'Annual not available — pick Monthly.' : 'Not available right now.'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-[#72A4BF]/70 mt-8 text-center">
          Payments are processed securely by Stripe. You can change or cancel your plan anytime from Settings.
        </p>
      </main>
    </div>
  );
}
